import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Users, Loader2, ArrowLeft, ChevronUp, ChevronDown, ChevronsUpDown, CalendarDays } from "lucide-react";
import { api } from "../../lib/api";
import { CustomerListResponse } from "../../features/customers/types";
import { RfmBadge } from "../../components/RfmBadge";
import { useInicio } from "../../lib/permisos";

function formatCurrency(n?: number): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-VE", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(d?: string): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-ES", { year: "numeric", month: "short", day: "2-digit" });
}

// Columnas ordenables: key = el valor que entiende el backend
type SortKey = "cod_cliente" | "nombre" | "ciudad" | "segment_label" | "ltv" | "ultima_compra";

export function CustomersListPage() {
  const inicio = useInicio();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>("cod_cliente");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const limit = 50;

  // El backend usa "campo" (asc) o "-campo" (desc)
  const sortParam = `${sortDir === "desc" ? "-" : ""}${sortKey}`;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["customers", search, page, sortParam],
    queryFn: () =>
      api<CustomerListResponse>(
        `/v1/customers?limit=${limit}&offset=${page * limit}&sort=${sortParam}${search ? `&search=${encodeURIComponent(search)}` : ""}`,
      ),
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronsUpDown className="size-3.5 opacity-30" />;
    return sortDir === "asc"
      ? <ChevronUp className="size-3.5 text-brand-600" />
      : <ChevronDown className="size-3.5 text-brand-600" />;
  }

  // Encabezado clicable reutilizable
  function Th({ col, label, align = "left" }: { col: SortKey; label: string; align?: "left" | "right" }) {
    return (
      <th className={`px-4 py-3 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
        <button
          onClick={() => toggleSort(col)}
          className={`inline-flex items-center gap-1 hover:text-brand-600 transition-colors ${align === "right" ? "flex-row-reverse" : ""} ${sortKey === col ? "text-brand-600" : ""}`}
        >
          {label}
          <SortIcon col={col} />
        </button>
      </th>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-in">

        {/* Barra superior con navegación */}
        <div className="flex items-center justify-between mb-5">
          <Link to={inicio} className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800 transition-colors">
            <ArrowLeft className="size-4" />
            Dashboard
          </Link>
          <Link to="/reservas" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-900">
            <CalendarDays className="size-4" />
            Reservas
          </Link>
        </div>

        {/* Header */}
        <header className="flex items-end justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-sm">
              <Users className="size-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-ink-800 leading-tight">Clientes 360°</h1>
              <p className="text-sm mt-0.5">
                {data?.fuente === "demo" && (
                  <span className="inline-flex items-center gap-1.5 text-warning font-medium">
                    <span className="size-1.5 rounded-full bg-warning animate-pulse" />
                    Modo DEMO — datos sintéticos
                  </span>
                )}
                {data?.fuente === "icg" && (
                  <span className="inline-flex items-center gap-1.5 text-success font-medium">
                    <span className="size-1.5 rounded-full bg-success" />
                    Conectado a ICG
                  </span>
                )}
              </p>
            </div>
          </div>
          {data && (
            <div className="text-right">
              <div className="text-3xl font-display font-bold text-ink-800 leading-none">{data.total.toLocaleString("es-ES")}</div>
              <div className="text-xs text-ink-500 mt-1">clientes</div>
            </div>
          )}
        </header>

        {/* Buscador */}
        <div className="card p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, CIF, email…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="input pl-10"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="p-16 flex items-center justify-center text-ink-400">
              <Loader2 className="size-6 animate-spin mr-2" />
              Cargando clientes…
            </div>
          ) : isError ? (
            <div className="p-8 text-danger">Error: {(error as Error).message}</div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-16 text-center text-ink-400">
              <Users className="size-12 mx-auto mb-2 opacity-30" />
              <p>Sin clientes que coincidan</p>
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="bg-ink-50 border-b border-ink-200">
                  <tr className="text-ink-600 text-xs uppercase tracking-wide">
                    <Th col="cod_cliente" label="Código" />
                    <Th col="nombre" label="Nombre" />
                    <th className="px-4 py-3 font-medium text-left">Contacto</th>
                    <Th col="ciudad" label="Ciudad" />
                    <Th col="segment_label" label="Segmento" />
                    <Th col="ltv" label="LTV" align="right" />
                    <Th col="ultima_compra" label="Última compra" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((c) => (
                    <tr key={c.codCliente} className="border-b border-ink-100 hover:bg-brand-50/30 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-ink-500">
                        <Link to={`/customers/${c.codCliente}`} className="hover:text-brand-600">#{c.codCliente}</Link>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/customers/${c.codCliente}`} className="font-medium text-ink-800 hover:text-brand-600">{c.nombre}</Link>
                        {c.alias && <div className="text-xs text-ink-500">{c.alias}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="text-ink-700">{c.email ?? "—"}</div>
                        <div className="text-ink-500">{c.telefono ?? "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-700">{c.ciudad ?? "—"}</td>
                      <td className="px-4 py-3"><RfmBadge label={c.segmentLabel} /></td>
                      <td className="px-4 py-3 text-right font-mono text-ink-800">{formatCurrency(c.ltv)}</td>
                      <td className="px-4 py-3 text-xs text-ink-600">{formatDate(c.ultimaCompra)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between px-4 py-3 bg-ink-50 border-t border-ink-200">
                <span className="text-xs text-ink-500">
                  Mostrando {page * limit + 1}-{Math.min((page + 1) * limit, data.total)} de {data.total.toLocaleString("es-ES")}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary disabled:opacity-50">Anterior</button>
                  <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= data.total} className="btn-secondary disabled:opacity-50">Siguiente</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
